import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import LoadingSpinner from "./loading-spinner"

const buttonVariants = cva(
  "inline-flex items-center active:scale-[0.97] cursor-pointer justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const spinnerVariants = cva(
  'border-t-button-disabled text-button-disabled-text mr-2 border-2',
  {
    variants: {
      size: {
        default: 'size-4 max-h-4 min-h-4 min-w-4 max-w-4',
        sm: 'size-3 max-h-3 min-h-3 min-w-3 max-w-3',
        lg: 'size-5 max-h-5 min-h-5 min-w-5 max-w-5',
        icon: 'size-3 max-h-3 min-h-3 min-w-3 max-w-3',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
  loadingSpinnerClassname?: string;
  children?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, disabled, loadingText, loadingSpinnerClassname, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <LoadingSpinner className={cn(spinnerVariants({ size }), loadingSpinnerClassname)} />
        )}
        { }
        <>{(loading && size !== 'icon') ? (loadingText ?? 'Gönderiliyor...') : (loading && size === 'icon') ? null : children}</>
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
